import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import OtpInput from "otp-input-react";
import { auth } from "../components/firebase";
import { signInWithPhoneNumber, RecaptchaVerifier } from "firebase/auth";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import AxiosInstance from "../context/axiosInstance";
import { getFcmToken } from "../components/firebase";
import { UserPlus, ShieldCheck, Loader2, X } from "lucide-react";
  const FormInput = ({ label, ...props }) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-400 mb-2">
        {label}
      </label>
      <input
        {...props}
        className="w-full h-12 bg-white/10 border border-white/20 rounded-lg px-4 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
      />
    </div>
  );
const SignupModal = ({ phone, setShowModal }) => {
  const [fullName, setFullName] = useState("");
  const [aadhaar6, setAadhaar6] = useState("");
  const [dob, setDob] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const navigate = useNavigate();
  const { setAccessToken, setRefreshToken, setUserID, setIsAuthenticated } =
    useAppContext();
  useEffect(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        { size: "invisible" }
      );
    }
  }, []);

const registerUser = async (formattedPhone) => {
  try {
    // 1. Call the /signup endpoint
    const signupResponse = await AxiosInstance.post("/auth/users/", {
      full_name: fullName,
      mobile_number: formattedPhone,
      aadhaar6: aadhaar6,
      dob: dob,
    });

    const { accessToken, refreshToken, profile } = signupResponse.data;
    const userID = profile.user_id;

    if (!userID || !accessToken || !refreshToken) {
      toast.error("Registration failed. Incomplete data received.");
      return;
    }

    // 2. Set all values in the context. This will update the state for all subsequent renders
    // and save to localStorage via useEffect.
    setUserID(userID);
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);
    setIsAuthenticated(true);

    // 3. Save FCM Token (this call does not require authentication)
    const fcmToken = await getFcmToken();
    if (fcmToken) {
      await AxiosInstance.post("/auth/fcm-tokens/", {
        token: fcmToken,
        phone: formattedPhone,
      });
    }

    // ✅ 4. THE FIX: For the immediate next call that needs auth, pass the token manually.
    // The global `api` instance's interceptor won't have the new token yet.
    await AxiosInstance.post(
      "/auth/invitations/link/",
      {
        user_id: userID,
        contact: formattedPhone,
      },
      {
        // Manually set the header for this one specific request.
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    // 5. All tasks are successful.
    toast.success("Registration successful!");
    navigate("/app");
    setShowModal(false);
  } catch (error) {
    console.error("Register user error:", error);
    const errorMessage =
      error.response?.data?.error || "Error registering user.";
    toast.error(errorMessage);
  }
};

  const sendOtp = () => {
    setLoading(true);
    const formattedPhone = phone.startsWith("+") ? phone : `+${phone}`;
    signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier)
      .then((confirmation) => {
        setConfirmationResult(confirmation);
        setShowOtpScreen(true);
        toast.success("OTP sent!");
      })
      .catch(() => toast.error("Failed to send OTP."))
      .finally(() => setLoading(false));
  };

  const verifyOtp = () => {
    if (!confirmationResult)
      return toast.error("OTP not requested. Please try again.");
    setLoading(true);
    confirmationResult
      .confirm(otp)
      .then(() => {
        toast.success("OTP verified! Registering your account...");
        const formattedPhone = phone.startsWith("+") ? phone : `+${phone}`;
        registerUser(formattedPhone);
      })
      .catch(() => toast.error("Invalid OTP."))
      .finally(() => setLoading(false));
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  };
  const contentVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
  };



  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-md p-4 transition-all duration-300">
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="relative w-full max-w-md bg-surface/90 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl shadow-black/50 p-8"
      >
        <button
          onClick={() => setShowModal(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-full p-2"
        >
          <X size={20} />
        </button>

        <AnimatePresence mode="wait">
          {showOtpScreen ? (
            <motion.div
              key="otp"
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mb-6 ring-4 ring-primary-500/10">
                <ShieldCheck size={32} className="text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Verify Your Number
              </h2>
              <p className="text-gray-400 mb-6 max-w-[80%]">
                Enter the 6-digit code sent to{" "}
                <span className="text-white font-medium">{phone.startsWith("+") ? phone : `+${phone}`}</span>
              </p>

              <OtpInput
                value={otp}
                onChange={setOtp}
                OTPLength={6}
                otpType="number"
                inputClassName="!w-full !max-w-[3rem] aspect-square bg-white/5 border border-white/10 rounded-xl text-white text-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-all placeholder-gray-600"
                containerClassName="w-full flex gap-3 justify-center"
              />
              <button
                onClick={verifyOtp}
                disabled={loading}
                className="w-full mt-8 bg-white text-black font-bold py-4 rounded-xl hover:bg-gray-100 transition-all flex items-center justify-center disabled:opacity-50 hover:shadow-lg hover:scale-[1.02]"
              >
                {loading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  "Verify & Register"
                )}
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="details"
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="flex flex-col"
            >
              <div className="flex flex-col items-center text-center mb-8">
                <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mb-4 ring-4 ring-primary-500/10">
                  <UserPlus size={32} className="text-primary-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  Create Your Account
                </h2>
                <p className="text-gray-400 text-sm">
                  Please provide a few details to get started.
                </p>
              </div>

              <div className="space-y-4">
                <FormInput
                  label="Full Name"
                  type="text"
                  placeholder="Enter your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
                <FormInput
                  label="Date of Birth"
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                />
                <FormInput
                  label="Last 6 Digits of Aadhaar"
                  type="text"
                  placeholder="xxxxxx"
                  value={aadhaar6}
                  onChange={(e) => setAadhaar6(e.target.value)}
                  maxLength={6}
                />
              </div>

              <div className="flex items-center gap-3 mt-4 mb-6">
                <input
                  type="checkbox"
                  id="terms"
                  checked={isChecked}
                  onChange={() => setIsChecked(!isChecked)}
                  className="h-5 w-5 bg-transparent border-gray-500 rounded text-primary-500 focus:ring-primary-500 focus:ring-offset-black cursor-pointer"
                />
                <label htmlFor="terms" className="text-sm text-gray-400 cursor-pointer select-none">
                  I agree to the{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    className="text-primary-400 hover:text-primary-300 hover:underline transition-colors"
                  >
                    Terms and Conditions
                  </a>
                  .
                </label>
              </div>

              <button
                onClick={sendOtp}
                disabled={
                  !fullName ||
                  !dob ||
                  aadhaar6.length !== 6 ||
                  !isChecked ||
                  loading
                }
                className="w-full bg-white text-black font-bold py-4 rounded-xl hover:bg-gray-100 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:scale-[1.02]"
              >
                {loading ? <Loader2 className="animate-spin" /> : "Register"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <div id="recaptcha-container"></div>
    </div>
  );
};

export default SignupModal;
